import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import API from '@/utils/api';

const AddToListDialog = ({ open, onOpenChange, favorite, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [lists, setLists] = useState([]);
  const [selectedList, setSelectedList] = useState('');

  useEffect(() => {
    if (open) {
      fetchLists();
    }
  }, [open]);

  const fetchLists = async () => {
    try {
      const response = await API.get('/shopping/lists?status=active');
      setLists(response.data);
      if (response.data.length > 0) {
        setSelectedList(response.data[0].id);
      }
    } catch (error) {
      console.error('Failed to fetch lists:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedList) {
      toast.error('Please select a list');
      return;
    }
    
    setLoading(true);
    try {
      await API.post(`/shopping/favorites/${favorite.id}/add-to-list?list_id=${selectedList}`);
      toast.success('Added to list!');
      onSuccess();
    } catch (error) {
      console.error('Failed to add to list:', error);
      toast.error('Failed to add to list');
    } finally {
      setLoading(false);
    }
  };

  if (!favorite) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[400px]">
        <DialogHeader>
          <DialogTitle>Add to Shopping List</DialogTitle>
          <DialogDescription>
            Add "{favorite.brand ? `${favorite.brand} - ` : ''}{favorite.name}" to a list
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label>Select List</Label>
            <Select value={selectedList} onValueChange={setSelectedList}>
              <SelectTrigger>
                <SelectValue placeholder="Choose a list..." />
              </SelectTrigger>
              <SelectContent>
                {lists.map(list => (
                  <SelectItem key={list.id} value={list.id}>
                    {list.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          {lists.length === 0 && (
            <p className="text-sm text-gray-500">
              No active lists. Create a list first.
            </p>
          )}
          
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button 
              type="submit" 
              disabled={loading || !selectedList}
              className="bg-[#D4E5C7] hover:bg-[#C4D5B7] text-gray-900"
            >
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Add to List
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AddToListDialog;
